import axios from 'axios';
import Qs from 'qs';
import {
  DocType,
  FileNameSearchResponse,
  PostSurveyResponse,
  SearchScope,
  SurveyExecutionStatus,
  SurveyExecutionStatusEntity,
  SurveyHistoryEntity,
  SurveySessionResponse,
  SurveyStatusEntity,
} from '@/types';
import { handleStreamingResponse } from '@/utils/streaming';
import { convertKeysToCamelCase } from '@/utils/survey/common';
import { STREAMING_DATA_TYPES } from '@/utils/survey/constants';
import { userSession } from '@/utils/userSession';

export async function* postSurvey(
  sessionId: string,
  question: string,
  searchScope: SearchScope,
  docTypes: Array<DocType | 'user_document'>,
  cachedExpandedQueries: string[],
  specifiedFiles: Array<FileNameSearchResponse>,
  deepMode: boolean,
  groupId?: number,
  groupTagIds?: Array<number>,
  signal?: AbortSignal,
): AsyncGenerator<PostSurveyResponse> {
  if (globalThis.fetch === undefined) return; // テスト実行でfetchはundefinedなのでreturn

  const config = {
    url: `api/v1/surveys/${sessionId}`,
    method: 'POST' as const,
    body: JSON.stringify({
      user_id: userSession.getUserId(),
      question,
      specified_files: specifiedFiles.length > 0 ? specifiedFiles : undefined,
      search_scope: searchScope,
      doc_types: docTypes.length > 0 ? docTypes : undefined,
      cached_expanded_queries: cachedExpandedQueries,
      deep_mode: deepMode,
      group_id: groupId,
      group_tags: groupTagIds,
    }),
    acceptedTypes: Object.values(STREAMING_DATA_TYPES),
    signal,
  };
  yield* handleStreamingResponse<PostSurveyResponse>(config);
}

export const fetchSurvey = async (surveySessionId: string) => {
  const { data } = await axios.get<SurveySessionResponse>(
    `/surveys/${surveySessionId}`,
  );
  return data;
};

export const fetchSurveyHistories = async (
  surveySessionId: string,
  page: number,
  limit: number,
  order: 'desc' | 'asc', // created_atの昇順・降順を指定
) => {
  const params = {
    page,
    limit,
    order,
  };

  const { data } = await axios.get<Array<SurveyHistoryEntity>>(
    `/surveys/${surveySessionId}/histories?${Qs.stringify(params, {
      arrayFormat: 'brackets',
    })}`,
  );
  return data;
};

export const fetchSurveyStatus = async (surveySessionId: string) => {
  const { data } = await axios.get<SurveyStatusEntity>(
    `/surveys/${surveySessionId}/status`,
  );

  return data;
};

export const createSurveySession = async (
  question: string,
  shareSurveyUuid?: string,
) => {
  const { data } = await axios.post<SurveySessionResponse>('/surveys', {
    title: question,
    share_survey_uuid: shareSurveyUuid,
  });
  return data;
};

export const fetchSurveys = async (page: number, limit: number) => {
  const params = { page, limit };

  const { data } = await axios.get(
    `/surveys?${Qs.stringify(params, {
      arrayFormat: 'brackets',
    })}`,
  );
  return data;
};

export const deleteSurveySession = async (surveySessionId: string) => {
  await axios.delete(`/surveys/${surveySessionId}`);
};

export const postSurveyFeedback = async (
  surveySessionId: string,
  requestId: string,
  evaluation: 'positive' | 'negative',
  reasons?: string[],
  comment?: string,
): Promise<void> => {
  await axios.post<void>(
    `/surveys/${surveySessionId}/histories/${requestId}/feedback`,
    {
      evaluation,
      reasons,
      comment,
    },
  );
};

export const deleteSurveyFeedback = async (
  surveySessionId: string,
  requestId: string,
): Promise<void> => {
  await axios.delete<void>(
    `/surveys/${surveySessionId}/histories/${requestId}/feedback`,
  );
};

/**
 * 他画面に遷移したなどで、生成途中のpostSurveyの接続が切れた場合に再接続するためのasync generator
 * postSurveyで登録されたデータをバックエンドではRDBをポーリングして取得したものを返している
 *
 * @param surveySessionId - surveysテーブルのUUID
 * @param requestId - surevy_requestsテーブルのUUID
 */
export async function* reconnectPostSurvey(
  sessionId: string,
  requestId: string,
  signal?: AbortSignal,
): AsyncGenerator<PostSurveyResponse> {
  const url = `/api/v1/surveys/${sessionId}/status/${requestId}/stream`;
  const config = {
    url,
    method: 'GET' as const,
    acceptedTypes: Object.values(STREAMING_DATA_TYPES),
    signal,
  };
  yield* handleStreamingResponse<PostSurveyResponse>(config);
}

/**
 * エラーや停止したブロックの再生成を行うasync generator
 */
export async function* regeneratePostSurvey(
  sessionId: string,
  requestId: string,
  signal?: AbortSignal,
): AsyncGenerator<PostSurveyResponse> {
  if (globalThis.fetch === undefined) return; // テスト実行でfetchはundefinedなのでreturn

  const config = {
    url: `api/v1/surveys/${sessionId}/regenerate/${requestId}`,
    method: 'POST' as const,
    body: JSON.stringify({
      user_id: userSession.getUserId(),
    }),
    acceptedTypes: Object.values(STREAMING_DATA_TYPES),
    signal,
  };
  yield* handleStreamingResponse<PostSurveyResponse>(config);
}

export const cancelSurvey = async (
  surveySessionId: string,
  requestId: string,
): Promise<void> => {
  await axios.post<void>(
    `/surveys/${surveySessionId}/status/${requestId}/cancel`,
  );
};

/**
 * surveyの各スレッドの実行状況と、ディープリサーチの同時実行状況
 */
export const fetchSurveyExecutionStatus =
  async (): Promise<SurveyExecutionStatus> => {
    const { data } = await axios.get<SurveyExecutionStatusEntity>(
      '/surveys/execution_status',
    );

    // スネークケース → キャメルケースに変換
    const convertedData = convertKeysToCamelCase(data, true);

    return convertedData as SurveyExecutionStatus;
  };

export const createSurveyPromptTemplateFavorite = async (
  surveyPromptTemplateId: number,
) => {
  await axios.post<void>(
    `/surveys/prompt_templates/${surveyPromptTemplateId}/favorites`,
  );
};

export const deleteSurveyPromptTemplateFavorite = async (
  surveyPromptTemplateId: number,
) => {
  await axios.delete<void>(
    `/surveys/prompt_templates/${surveyPromptTemplateId}/favorites`,
  );
};

export const createSurveyPromptTemplateRequest = async (content: string) => {
  await axios.post<void>(`/surveys/prompt_templates/requests`, {
    content,
  });
};
